import React, { useEffect, useRef, useState } from 'react';
import { Pill } from 'lucide-react';
import { anamnesiVociAPI } from '../../services/api';
import { annunciaAnamnesiCambiata } from '../../utils/annunci';
import { messaggioDa } from '../../utils/notifiche';
import { useModificaVoce } from '../../utils/modificaVoce';
import { sipuoChiudere, conInvio } from '../../utils/salvaCampo';
import { useUscitaProtetta } from '../../utils/uscitaProtetta';
import CampoMeseAnno from '../CampoMeseAnno';
import StatoSalvataggio, { FraseRigaNuova } from '../StatoSalvataggio';

/**
 * TerapieInCorso — la sezione «Terapie in corso» dell'anamnesi, a tabella.
 *
 * Una riga = una voce della sezione «farmaci» (migration 233): lo stesso
 * meccanismo delle altre voci - autore, data, registro modifiche, cestino,
 * cancellazione selettiva - con tre colonne in piu'.
 *
 *   Farmaco   il testo della voce, obbligatorio;
 *   Dosaggio  testo libero, facoltativo. Nessun calcolo e nessun avviso:
 *             il gestionale registra, non giudica;
 *   Inizio    mese e anno (03/2024) o solo l'anno (2024), facoltativo;
 *             mai nel futuro e mai prima della nascita (lo rifiutano il
 *             server e, sotto, il database - migration 275). Fino al
 *             30/09/2026 era un campo «mese» del browser, che Firefox e
 *             Safari sul computer non hanno: chi scriveva «03/2024» mandava
 *             un valore vuoto, e la colonna restava «—».
 *   Stop      la strada «superata» che esiste gia': chi e quando. Togliendo
 *             la spunta la voce si riattiva.
 *   Note      (migration 342, Alex 26/10/2026) testo libero su piu' righe,
 *             facoltativo, una nota per riga. Non e' una colonna della
 *             tabella: si scrive in una riga sotto i campi (nella riga nuova
 *             si salva con «Aggiungi terapia», in «Modifica» da sola uscendo
 *             dal campo, come gli altri) e si legge sotto la riga, in testo
 *             piccolo, sopra «inserita il…». Nella riga nuova Invio va a
 *             capo (si salva solo col pulsante); in «Modifica» vale la regola
 *             di ogni testo che si salva da solo (Alex, 14/10/2026, conInvio):
 *             Invio salva e il cursore resta li', Maiusc+Invio va a capo -
 *             come il testo di una voce dell'anamnesi.
 *
 * Una terapia sospesa RESTA nella tabella, in grigio e spuntata: chi legge
 * deve sapere che quel farmaco c'era e che e' stato sospeso, e da chi.
 *
 * Le voci scritte prima della tabella con piu' farmaci in un testo solo
 * portano il segno «da dividere»: le divide a mano il medico o
 * l'amministratore, nessuno script interpreta il testo.
 */

const soloData = (v) => {
    if (!v) return null;
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

const meseAnno = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})/);
    return m ? `${m[2]}/${m[1]}` : '';
};

/** L'inizio come si legge in tabella: «03/2024», o «2024» se si sa solo l'anno.
 *  Lo usa anche il riquadro «Terapie in corso» del riepilogo della cartella
 *  (RiepilogoCartella): una funzione sola, cosi' le due tabelle non
 *  scrivono la stessa data in due modi. */
export const inizioScritto = (v) => (v.inizio_solo_anno ? (v.inizio_anno || '') : meseAnno(v.inizio_mese));



const VUOTA = { testo: '', dosaggio: '', inizio: '', nota: '' };

const TerapieInCorso = ({ pazienteId, voci, modificabile, autoreDi, onCambiata, onErrore, onElimina,
                         apriNuova = false, onNuovaAperta }) => {
    const [nuova, setNuova] = useState(null);       // { testo, dosaggio, inizio }
    /* «+ Aggiungi» del riepilogo della cartella (stile «Console», 6/10/2026)
       porta qui con la riga nuova gia' aperta: e' lo stesso «＋ Aggiungi» di
       questa tabella, non una seconda strada - la riga si salva con
       «Aggiungi terapia», come sempre. La richiesta si consuma una volta
       (onNuovaAperta): tornando sull'anamnesi la riga non si riapre. */
    const sezione = useRef(null);
    useEffect(() => {
        if (!apriNuova) return;
        setNuova((n) => n || { ...VUOTA });
        if (onNuovaAperta) onNuovaAperta();
        if (sezione.current && sezione.current.scrollIntoView) sezione.current.scrollIntoView({ block: 'center' });
        // eslint-disable-next-line
    }, [apriNuova]);
    const [modifica, setModifica] = useState(null); // { id, testo, dosaggio, inizio }
    const [salvataggio, setSalvataggio] = useState(false);
    /* Aperta dal «—» della colonna Inizio: il cursore va li'. */
    const [suInizio, setSuInizio] = useState(false);

    const esegui = async (azione, ripiego) => {
        onErrore(null);
        try {
            setSalvataggio(true);
            await azione();
            onCambiata();
            annunciaAnamnesiCambiata();
            return true;
        } catch (e) {
            onErrore(messaggioDa(e, ripiego));
            return false;
        } finally {
            setSalvataggio(false);
        }
    };

    const salvaNuova = async () => {
        if (!nuova?.testo?.trim()) return;
        const ok = await esegui(() => anamnesiVociAPI.crea(pazienteId, {
            sezione: 'farmaci',
            testo: nuova.testo,
            dosaggio: nuova.dosaggio,
            inizio: nuova.inizio,
            nota: nuova.nota,
        }), 'Errore nel salvataggio');
        if (ok) setNuova(null);
    };

    /* LA MODIFICA DI UNA RIGA SI SALVA DA SOLA, campo per campo, quando si
       esce dal campo (regola di Alex del 24/09/2026): niente «Salva», si
       chiude con «Chiudi». La riga nuova resta «Aggiungi terapia». */
    /* LA TERAPIA NUOVA resta un pulsante («Aggiungi terapia» crea una riga
       firmata), ma quello che e' scritto non si perde in silenzio: domanda al
       cambio di schermata e avviso del browser, come per ogni modulo. */
    useUscitaProtetta(!!nuova && !!(nuova.testo.trim() || nuova.dosaggio.trim() || nuova.inizio || nuova.nota.trim()));
    /* Smontaggio con la modifica aperta (cambio di linguetta, Ctrl K): quello
       che e' scritto si salva comunque, se differisce dall'archivio. */
    const modificaOra = useRef(null);
    modificaOra.current = modifica;
    const auto = useModificaVoce(annunciaAnamnesiCambiata, () => (modificaOra.current ? {
        testo: modificaOra.current.testo,
        dosaggio: modificaOra.current.dosaggio,
        inizio: modificaOra.current.inizio,
        nota: modificaOra.current.nota,
    } : null));
    const apriModifica = (v, inizio = false) => {
        const valori = { testo: v.testo, dosaggio: v.dosaggio || '', inizio: inizioScritto(v), nota: v.nota || '' };
        auto.inizia(v.id, valori);
        setSuInizio(inizio);
        setModifica({ id: v.id, ...valori });
    };
    const chiudiModifica = () => {
        if (!sipuoChiudere(auto.inCorso, auto.nonSalvati.length)) return;
        setModifica(null); onCambiata();
    };

    const cambiaStop = (v, spunta) => esegui(
        () => (spunta ? anamnesiVociAPI.supera(v.id, null) : anamnesiVociAPI.riattiva(v.id)),
        'Errore nell\'operazione'
    );

    const chiHaSospeso = (v) => {
        if (v.superata_da_cognome) return `Dr. ${v.superata_da_cognome}`;
        const nome = [v.superata_utente_nome, v.superata_utente_cognome].filter(Boolean).join(' ');
        return nome || 'non registrato';
    };

    /* `esci`: solo in modifica, dove ogni campo si salva uscendo. */
    const campi = (stato, setStato, esci = null) => (
        <>
            <td>
                <input
                    type="text"
                    className="av-ter-input"
                    value={stato.testo}
                    onChange={(e) => setStato({ ...stato, testo: e.target.value })}
                    onBlur={esci ? (e) => esci('testo', e.target.value) : undefined}
                    onKeyDown={esci ? conInvio((e) => esci('testo', e.target.value)) : undefined}
                    placeholder="Farmaco"
                    aria-label="Farmaco"
                    autoFocus={!(esci && suInizio)}
                />
            </td>
            <td>
                <input
                    type="text"
                    className="av-ter-input"
                    value={stato.dosaggio}
                    onChange={(e) => setStato({ ...stato, dosaggio: e.target.value })}
                    onBlur={esci ? (e) => esci('dosaggio', e.target.value) : undefined}
                    onKeyDown={esci ? conInvio((e) => esci('dosaggio', e.target.value)) : undefined}
                    placeholder="facoltativo"
                    aria-label="Dosaggio"
                    maxLength={200}
                />
            </td>
            <td>
                {/* CampoMeseAnno, il componente unico dei mesi: numeri, la
                    barra la mette lui, tastiera numerica; qui anche l'anno solo. */}
                <CampoMeseAnno
                    soloAnno
                    className="av-ter-input av-ter-inizio"
                    value={stato.inizio}
                    onChange={(t) => setStato({ ...stato, inizio: t })}
                    onBlur={esci ? (e) => esci('inizio', e.target.value) : undefined}
                    onKeyDown={esci ? conInvio((e) => esci('inizio', e.target.value)) : undefined}
                    aria-label="Inizio (mese e anno, oppure solo l'anno)"
                    title="Mese e anno (03/2024) oppure solo l'anno (2024), facoltativo"
                    autoFocus={!!(esci && suInizio)}
                />
            </td>
        </>
    );

    /* La nota sta in una riga sua, larga quanto la tabella: in una colonna
       accanto all'inizio una textarea a 390 px non si leggerebbe. Niente
       maxLength, di proposito: il browser taglierebbe in silenzio una nota
       incollata piu' lunga di 2000 caratteri. Il limite lo dice il server
       col suo messaggio, e il testo resta nel campo. */
    const rigaNota = (stato, setStato, esci = null) => (
        <tr className="av-ter-riga-nota">
            <td colSpan={4}>
                <label className="av-ter-nota-etichetta">
                    <span>Note</span>
                    <textarea
                        className="av-ter-input av-ter-nota-campo"
                        value={stato.nota}
                        onChange={(e) => setStato({ ...stato, nota: e.target.value })}
                        onBlur={esci ? (e) => esci('nota', e.target.value) : undefined}
                        onKeyDown={esci ? conInvio((e) => esci('nota', e.target.value)) : undefined}
                        placeholder="facoltativa"
                        aria-label="Note della terapia"
                        rows={2}
                    />
                </label>
            </td>
        </tr>
    );

    const pulsantiSalva = (onAnnulla, onSalva, etichetta, testo) => (
        <div className="av-azioni-modifica">
            <button className="av-btn-annulla" onClick={onAnnulla} disabled={salvataggio}>Annulla</button>
            <button className="av-btn-salva" onClick={onSalva} disabled={salvataggio || !testo.trim()}>
                {salvataggio ? 'Salvataggio…' : etichetta}
            </button>
        </div>
    );

    return (
        <div className="av-sezione" ref={sezione}>
            <div className="av-sezione-testa">
                <h3><Pill size={16} strokeWidth={1.8} aria-hidden="true" />Terapie in corso</h3>
                <button
                    className="av-btn-aggiungi"
                    onClick={() => setNuova({ ...VUOTA })}
                    disabled={!!nuova}
                >
                    ＋ Aggiungi
                </button>
            </div>

            {voci.length === 0 && !nuova ? (
                <p className="av-vuoto">Nessuna terapia registrata.</p>
            ) : (
                <table className="av-ter-tabella">
                    <colgroup>
                        <col className="av-ter-col-farmaco" />
                        <col className="av-ter-col-dosaggio" />
                        <col className="av-ter-col-inizio" />
                        <col className="av-ter-col-stop" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Farmaco</th>
                            <th>Dosaggio</th>
                            <th>Inizio</th>
                            <th className="av-ter-stop">Stop</th>
                        </tr>
                    </thead>
                    {voci.map((v) => {
                        const sospesa = v.stato === 'superata';

                        if (modifica?.id === v.id) {
                            return (
                                <tbody key={v.id} className="av-ter-voce av-ter-in-modifica" data-salva-da-solo>
                                    <tr>
                                        {campi(modifica, setModifica, auto.esci)}
                                        <td />
                                    </tr>
                                    {rigaNota(modifica, setModifica, auto.esci)}
                                    <tr className="av-ter-piede">
                                        <td colSpan={4}>
                                          <div className="av-voce-piede">
                                            {v.da_dividere && (
                                                <span className="av-ter-dividere-aiuto">
                                                    Lascia qui un farmaco solo e aggiungi gli altri con «＋ Aggiungi».
                                                </span>
                                            )}
                                            <div className="av-azioni-modifica">
                                                <button className="av-btn-annulla" onClick={chiudiModifica} disabled={auto.inCorso}>Chiudi</button>
                                            </div>
                                          </div>
                                          <StatoSalvataggio campi={['testo', 'dosaggio', 'inizio', 'nota']} stato={auto.stato} salvatoAlle={auto.salvatoAlle}
                                              onRiprova={() => auto.erroriAperti.forEach((x) => auto.riprova(x.campo, modifica[x.campo]))} />
                                          {auto.erroriAperti.map((x) => (
                                              <div key={x.campo} className="av-errore-campo" role="alert">
                                                  <span>Non salvato: {x.messaggio}. Il testo è ancora nel campo.</span>
                                                  <button type="button" className="av-link"
                                                          onClick={() => auto.riprova(x.campo, modifica[x.campo])}>Riprova</button>
                                              </div>
                                          ))}
                                        </td>
                                    </tr>
                                </tbody>
                            );
                        }

                        return (
                            <tbody key={v.id} className={`av-ter-voce ${sospesa ? 'av-ter-sospesa' : ''}`}>
                                <tr>
                                    <td className="av-ter-farmaco">
                                        <span className="av-testo">{v.testo}</span>
                                        {v.da_dividere && (
                                            <span
                                                className="av-ter-dividere"
                                                title="Scritta prima della tabella, con più farmaci in una voce sola: va divisa, una riga per farmaco"
                                            >
                                                da dividere
                                            </span>
                                        )}
                                    </td>
                                    <td>{v.dosaggio || <span className="av-ter-vuoto">—</span>}</td>
                                    <td>
                                        {inizioScritto(v) || (!sospesa && modificabile(v)
                                            /* Un clic sul «—» apre la modifica della riga, col
                                               cursore sull'inizio (Alex, 30/09/2026). */
                                            ? <button type="button" className="av-ter-vuoto av-ter-vuoto-link"
                                                      onClick={() => apriModifica(v, true)}
                                                      title="Scrivi l'inizio della terapia"
                                                      aria-label="Scrivi l'inizio della terapia">—</button>
                                            : <span className="av-ter-vuoto">—</span>)}
                                    </td>
                                    <td className="av-ter-stop">
                                        <input
                                            type="checkbox"
                                            checked={sospesa}
                                            disabled={salvataggio}
                                            onChange={(e) => cambiaStop(v, e.target.checked)}
                                            aria-label={sospesa ? 'Riattiva la terapia' : 'Sospendi la terapia'}
                                            title={sospesa ? 'Togli la spunta per riattivarla' : 'Spunta per segnare la terapia come sospesa'}
                                        />
                                    </td>
                                </tr>
                                <tr className="av-ter-piede">
                                    <td colSpan={4}>
                                      {v.nota && <div className="av-ter-nota">{v.nota}</div>}
                                      <div className="av-voce-piede">
                                        <span className="av-firma">
                                            {autoreDi(v)}
                                            {` · inserita il ${soloData(v.created_at) || '—'}`}
                                            {sospesa && (
                                                <span className="av-ter-sospesa-nota">
                                                    {` · sospesa · ${chiHaSospeso(v)} · ${soloData(v.superata_il) || '—'}`}
                                                </span>
                                            )}
                                        </span>
                                        <div className="av-voce-azioni">
                                            {!sospesa && modificabile(v) && (
                                                <button
                                                    className="av-link"
                                                    onClick={() => apriModifica(v)}
                                                >
                                                    Modifica
                                                </button>
                                            )}
                                            {modificabile(v) && (
                                                <button className="av-link av-link-pericolo" onClick={() => onElimina(v)}>
                                                    Elimina
                                                </button>
                                            )}
                                        </div>
                                      </div>
                                    </td>
                                </tr>
                            </tbody>
                        );
                    })}
                    {nuova && (
                        <tbody className="av-ter-voce av-ter-in-modifica">
                            <tr>
                                {campi(nuova, setNuova)}
                                <td />
                            </tr>
                            {rigaNota(nuova, setNuova)}
                            <tr className="av-ter-piede">
                                <td colSpan={4}>
                                  <FraseRigaNuova pulsante="Aggiungi terapia" />
                                  <div className="av-voce-piede">
                                    {pulsantiSalva(() => setNuova(null), salvaNuova, 'Aggiungi terapia', nuova.testo)}
                                  </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            )}
        </div>
    );
};

export default TerapieInCorso;
